import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import DashboardColumnTooltip from "../components/DashboardColumnTooltip.tsx";
import {
  StaticDashboard,
  EditableDashboard,
  useMetabaseAuthStatus,
  type DrillThroughQuestionProps,
  type ParameterChangePayload,
} from "@metabase/embedding-sdk-react";

export function parameterValuesEqual(
  left?: Record<string, any>,
  right?: Record<string, any>,
) {
  const leftKeys = Object.keys(left || {}).sort();
  const rightKeys = Object.keys(right || {}).sort();
  if (leftKeys.length !== rightKeys.length) return false;

  return leftKeys.every((key, index) => {
    if (key !== rightKeys[index]) return false;
    const leftValue = left?.[key];
    const rightValue = right?.[key];
    if (Array.isArray(leftValue) || Array.isArray(rightValue)) {
      if (!Array.isArray(leftValue) || !Array.isArray(rightValue)) return false;
      return leftValue.length === rightValue.length &&
        leftValue.every((value, valueIndex) => value === rightValue[valueIndex]);
    }
    return leftValue === rightValue;
  });
}

function SdkDashboardView({
  entityId,
  refreshSec,
  readOnly = true,
  withTitle,
  columnMetadata,
  embedParams,
  onParametersChange,
  onQuestionSave,
}: {
  onQuestionSave?: DrillThroughQuestionProps["onSave"];
  entityId: number;
  refreshSec?: number;
  readOnly?: boolean;
  withTitle?: boolean;
  columnMetadata?: unknown;
  embedParams?: {
    initialParameters?: Record<string, any>;
    parameters?: Record<string, any>;
    hiddenParameters?: string[];
    requiredParameters?: string[];
  } | null;
  onParametersChange?: (parameters: Record<string, any>) => void;
}) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const authStatus = useMetabaseAuthStatus();
  const sdkReady = authStatus?.status === "success";
  const [hasLoadedSdk, setHasLoadedSdk] = useState(false);
  const [focusedCard, setFocusedCard] = useState<{ key: string; title: string } | null>(null);
  const configuredParameters = embedParams?.parameters ?? embedParams?.initialParameters;
  const [controlledParameters, setControlledParameters] = useState<Record<string, any> | undefined>(
    configuredParameters,
  );
  const controlledParametersRef = useRef<Record<string, any> | undefined>(configuredParameters);

  useEffect(() => {
    setFocusedCard(null);
  }, [entityId, readOnly]);

  useEffect(() => {
    if (!sdkReady || !shellRef.current) return;
    const shell = shellRef.current;
    const makeTitlesKeyboardAccessible = () => {
      shell.querySelectorAll<HTMLElement>('[data-testid="dashcard"] [data-testid="legend-label"]')
        .forEach((title) => {
          title.setAttribute("role", "button");
          title.setAttribute("tabindex", "0");
          title.setAttribute("aria-label", `View ${title.textContent?.trim() || "report"}`);
        });
    };
    makeTitlesKeyboardAccessible();
    const observer = new MutationObserver(makeTitlesKeyboardAccessible);
    observer.observe(shell, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [sdkReady]);

  // Dashboards with auto-apply-filters disabled (kept off deliberately --
  // Metabase's own guidance is that auto-apply hurts performance) surface a
  // "N filters changed" toast the moment we inject the report's own required
  // defaults, before the user has touched anything. Confirm that toast once,
  // for the report's own initial values only -- a later toast from the user
  // actually changing a filter is left alone so they still control when an
  // expensive query re-runs.
  const initialFilterToastHandledEntityRef = useRef<number | null>(null);

  useEffect(() => {
    if (!sdkReady || !shellRef.current) return;
    const shell = shellRef.current;
    let handled = initialFilterToastHandledEntityRef.current === entityId;

    const autoApplyInitialFilterToast = () => {
      if (handled) return;
      const toast = shell.querySelector('[data-testid="filter-apply-toast"]');
      const applyButton = toast?.querySelector<HTMLButtonElement>('button[aria-label="Apply"]');
      if (!applyButton) return;
      handled = true;
      initialFilterToastHandledEntityRef.current = entityId;
      applyButton.click();
    };

    autoApplyInitialFilterToast();
    const observer = new MutationObserver(autoApplyInitialFilterToast);
    observer.observe(shell, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [sdkReady, entityId]);

  // A saved question loses dashboard-only parameter mappings when the SDK
  // navigates to it. Expand the existing card so its scoped query is retained.
  const focusClickedCard = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    const title = target.closest('[data-testid="legend-label"]');
    const card = title?.closest('[data-testid="dashcard"][data-dashcard-key]');
    const key = card?.getAttribute("data-dashcard-key");
    if (!key || !/^\d+$/.test(key)) return false;
    setFocusedCard({ key, title: title?.textContent?.trim() || "Report" });
    return true;
  }, []);

  const handleCardClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!focusClickedCard(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
  }, [focusClickedCard]);

  const handleCardKeyDownCapture = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (!focusClickedCard(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
  }, [focusClickedCard]);

  useEffect(() => {
    controlledParametersRef.current = configuredParameters;
    setControlledParameters(configuredParameters);
  }, [entityId, configuredParameters]);

  const handleParametersChange = useCallback(
    (payload: ParameterChangePayload) => {
      // Loading a dashboard and reapplying controlled props can echo Metabase
      // defaults. Only a user edit should replace a remembered selection.
      if (payload.source && payload.source !== "manual-change") return;
      const previous = controlledParametersRef.current || {};
      const next = { ...(payload.parameters || {}) };

      for (const name of embedParams?.requiredParameters || []) {
        const value = next[name];
        const isEmpty =
          value == null ||
          (typeof value === "string" && value.trim() === "") ||
          (Array.isArray(value) && value.length === 0);

        if (isEmpty) {
          next[name] = previous[name] ?? configuredParameters?.[name];
        }
      }

      // Auto-refresh can echo the current parameter payload. Feeding an
      // equivalent object back into the controlled SDK prop causes a second
      // dashboard update and a visible flash on short refresh intervals.
      // A rejected clear must be sent back to the SDK even when restoring
      // the previous value. Only skip genuine unchanged SDK payloads.
      if (
        parameterValuesEqual(previous, next) &&
        parameterValuesEqual(payload.parameters, next)
      ) return;

      controlledParametersRef.current = next;
      setControlledParameters(next);
      onParametersChange?.(next);
    },
    [configuredParameters, embedParams?.requiredParameters, onParametersChange],
  );

  const commonProps = useMemo(
    () => ({
      dashboardId: entityId,
      withDownloads: true,
      withTitle,
      parameters: controlledParameters,
      onParametersChange: handleParametersChange,
      hiddenParameters: embedParams?.hiddenParameters?.length ? embedParams.hiddenParameters : undefined,
      autoRefreshInterval: refreshSec && refreshSec > 0 ? refreshSec : undefined,
    }),
    [
      entityId,
      refreshSec,
      withTitle,
      controlledParameters,
      handleParametersChange,
      embedParams?.hiddenParameters,
    ],
  );

  useEffect(() => {
    if (sdkReady) {
      setHasLoadedSdk(true);
    }
  }, [sdkReady]);

  if (!sdkReady && !hasLoadedSdk) {
    return (
      <div ref={shellRef} className={`rt-dashboard-sdk ${readOnly ? "is-readonly" : "is-editable"}`}>
        {authStatus?.status === "error" ? (
          <div className="rt-sdk-loading">Unable to load the dashboard.</div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      ref={shellRef}
      className={`rt-dashboard-sdk ${readOnly ? "is-readonly" : "is-editable"}${focusedCard ? " is-focused-card" : ""}`}
      onClickCapture={handleCardClickCapture}
      onKeyDownCapture={handleCardKeyDownCapture}
    >
      {focusedCard && (
        <>
          <div className="rt-focused-card-toolbar">
            <button type="button" onClick={() => setFocusedCard(null)}>← Back to dashboard</button>
            <strong>{focusedCard.title}</strong>
          </div>
          <style>{`
            .rt-dashboard-sdk.is-focused-card [data-testid="dashcard-container"]:has([data-dashcard-key="${focusedCard.key}"]) {
              display: block !important;
              position: relative !important;
              inset: auto !important;
              transform: none !important;
              width: 100% !important;
              height: 100% !important;
            }
          `}</style>
        </>
      )}
      {readOnly
        ? <StaticDashboard {...commonProps} />
        : <EditableDashboard {...commonProps} drillThroughQuestionProps={{ onSave: onQuestionSave }} />}
      {process.env.REACT_APP_DASHBOARD_COLUMN_TOOLTIPS !== "false" && (
        <DashboardColumnTooltip shellRef={shellRef} metadata={columnMetadata} />
      )}
    </div>
  );
}

export default memo(SdkDashboardView);
